/**
 * Real Supabase call-timing instrumentation — not a simulation. Wraps the
 * app's single `supabase` client so every `.from(table)...` / `.rpc(fn)`
 * call is timed end-to-end (request start -> response resolved), which is
 * exactly "Supabase/RPC request latency" + "duplicate/repeated requests"
 * from the performance audit checklist. This sandbox's outbound network
 * is proxied and cannot reach *.supabase.co directly (only the Supabase
 * MCP tool's separate management-API path can), so real P50/P95 browser
 * timing could not be captured from here — this instrumentation exists so
 * the OWNER can capture it themselves, in the real browser, against the
 * real network path, the next time they load the app.
 *
 * Usage (browser DevTools console, on the real deployed app):
 *   window.__alexPerf.summary()   // grouped count/avg/p50/p95/max per call
 *   window.__alexPerf.recent(20)  // last N calls, newest first
 *
 * Implementation note: Supabase's query builder is a fluent API where each
 * chained method (.select/.eq/.order/...) returns a NEW builder object, so
 * a one-time monkey-patch of the object .from()/.rpc() first returns would
 * never see the final .then() the caller actually awaits. A Proxy that
 * re-wraps every chained call's return value is what makes this correct
 * regardless of how many methods are chained before the query resolves.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export interface PerfEntry {
  kind: 'from' | 'rpc';
  target: string;
  route: string;
  startedAt: number;
  durationMs: number;
  ok: boolean;
}

const log: PerfEntry[] = [];
const MAX_ENTRIES = 500;

function currentRoute(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '(unknown)';
}

function record(entry: PerfEntry) {
  log.push(entry);
  if (log.length > MAX_ENTRIES) log.shift();
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1);
  return sorted[Math.max(0, idx)];
}

function summary() {
  const groups = new Map<string, number[]>();
  for (const e of log) {
    const key = `${e.kind}:${e.target}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(e.durationMs);
  }
  const rows = Array.from(groups.entries())
    .map(([key, durations]) => {
      const sorted = [...durations].sort((a, b) => a - b);
      return {
        call: key,
        count: durations.length,
        avgMs: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
        p50Ms: Math.round(percentile(sorted, 50)),
        p95Ms: durations.length >= 3 ? Math.round(percentile(sorted, 95)) : NaN,
        maxMs: Math.round(sorted[sorted.length - 1]),
      };
    })
    .sort((a, b) => b.avgMs - a.avgMs);
  // eslint-disable-next-line no-console
  console.table(rows);
  return rows;
}

function recent(n = 20) {
  const rows = log
    .slice(-n)
    .reverse()
    .map((e) => ({ route: e.route, call: `${e.kind}:${e.target}`, durationMs: Math.round(e.durationMs), ok: e.ok }));
  // eslint-disable-next-line no-console
  console.table(rows);
  return rows;
}

if (typeof window !== 'undefined') {
  (window as unknown as { __alexPerf: unknown }).__alexPerf = { summary, recent };
}

interface ThenLike {
  then: (onfulfilled?: (v: { error: unknown }) => unknown, onrejected?: (e: unknown) => unknown) => unknown;
}

/** Recursively re-wraps every chained-method return value so the timing
 * survives an arbitrary-length .select().eq().order().limit() chain,
 * finally intercepting whichever .then() the caller actually awaits. */
function wrapBuilder<T extends object>(builder: T, kind: 'from' | 'rpc', target: string, startedAt: number): T {
  return new Proxy(builder, {
    get(obj, prop, receiver) {
      const value = Reflect.get(obj, prop, receiver);
      if (prop === 'then') {
        return (onfulfilled?: (v: unknown) => unknown, onrejected?: (e: unknown) => unknown) =>
          (value as ThenLike['then']).call(
            obj,
            (result: { error: unknown }) => {
              record({ kind, target, route: currentRoute(), startedAt, durationMs: performance.now() - startedAt, ok: !result?.error });
              return onfulfilled ? onfulfilled(result) : result;
            },
            onrejected
          );
      }
      if (typeof value === 'function') {
        return (...args: unknown[]) => {
          const result = (value as (...a: unknown[]) => unknown).apply(obj, args);
          if (result && typeof result === 'object' && typeof (result as Partial<ThenLike>).then === 'function') {
            return wrapBuilder(result as object, kind, target, startedAt);
          }
          return result;
        };
      }
      return value;
    },
  });
}

/** Wraps supabase.from()/rpc() to time every real call transparently.
 * Call once, right after creating the client. */
export function instrumentSupabaseClient(client: SupabaseClient): void {
  const originalFrom = client.from.bind(client);
  const originalRpc = client.rpc.bind(client);

  (client as unknown as { from: typeof client.from }).from = ((table: string) => {
    const startedAt = performance.now();
    return wrapBuilder(originalFrom(table) as object, 'from', table, startedAt);
  }) as typeof client.from;

  (client as unknown as { rpc: typeof client.rpc }).rpc = ((fn: string, params?: unknown, options?: unknown) => {
    const startedAt = performance.now();
    return wrapBuilder(originalRpc(fn, params as never, options as never) as object, 'rpc', fn, startedAt);
  }) as typeof client.rpc;
}
